import { type IconDefinition } from '@fortawesome/pro-light-svg-icons';
import { faCheck, faMinus } from '@fortawesome/pro-solid-svg-icons';
import { type FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import cx, { type ClassValue } from 'clsx';
import React, {
	forwardRef,
	useState,
} from 'react';

import { Div, Label } from '/react4xp/components/Element';
import { FontAwesomeIconWrapper } from '/react4xp/components/FontAwesomeIconWrapper';
import { customTwMerge } from '/react4xp/util/customTwMerge';


type InputAttributes = React.InputHTMLAttributes<HTMLInputElement>;

type ButtonCheckboxProps = Omit<InputAttributes, 'checked'|'className'|'id'|'name'> & {
	checked?: boolean | 'mixed';
	error?: boolean;
	icon?: IconDefinition;
	iconColor?: FontAwesomeIconProps['color'];
	id: string;
	className?: ClassValue;
	label?: string;
	name: string;
};


export const ButtonCheckbox = forwardRef<HTMLInputElement, ButtonCheckboxProps>((
	{
		disabled,
		error,
		icon,
		iconColor,
		id,
		checked,
		className,
		label,
		name,
		onChange,
		onKeyDown: onKeyDownProp,
		onKeyUp: onKeyUpProp,
		value,
		...otherInputProps
	},
	inputRef,
) => {
	const [stateActive, setStateActive] = useState(false);

	const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (event) => {
		if (event.code === 'Enter') {
			event.preventDefault(); // Avoid submitting the form
		}
		if (
			event.code === 'Enter'
			|| event.code === 'Space'
		) {
			setStateActive(true);
		}
		if (onKeyDownProp) onKeyDownProp(event);
	};

	const onKeyUp: React.KeyboardEventHandler<HTMLInputElement> = (event) => {
		if (
			event.code === 'Enter'
			|| event.code === 'Space'
		) {
			setStateActive(false);
		}
		if (onKeyUpProp) onKeyUpProp(event);
	};

	return (
		<div
			key={id}
		>
			<input
				{...otherInputProps}
				autoComplete='off' // Avoid scroll issue in Chrome
				checked={checked === 'mixed' ? true : checked}

				// className='peer sr-only' // No no, top of -1000px causes scrolling issue
				className='opacity-0 peer pointer-events-none size-0'

				disabled={disabled}
				id={id}
				name={name}
				onChange={onChange}
				onKeyDown={onKeyDown}
				onKeyUp={onKeyUp}
				ref={inputRef}
				type='checkbox'
				value={value}
			/>
			<Label
				htmlFor={id}
				className={customTwMerge(cx(
					stateActive
						? 'bg-surface-action-secondary-active'
						: checked
							? 'bg-surface-feedback-success'
							: 'bg-surface-action-secondary',
					checked ? 'text-stroke-thin' : undefined,
					checked ?
						disabled ? 'text-stroke-[#A6A6A6]' : 'text-stroke-[#002949]'
						: undefined,
					stateActive ? 'not-[:disabled]:scale-[0.98]' : undefined,
					'active:bg-surface-action-secondary-active',
					'active:not-[:disabled]:scale-[0.98]',
					'active:shadow-[inset_0_0_0_2px_rgb(2,81,105)]',
					disabled ? 'cursor-not-allowed' : 'cursor-pointer',
					'duration-200',
					'gap-xs',
					'hover:bg-surface-action-secondary-hover',
					'inline-flex',
					'items-center',
					'justify-center',
					'p-sm',
					'peer-focus-visible:outline-2',
					'peer-focus-visible:outline',
					'peer-focus-visible:outline-offset-2',
					{ 'pointer-events-none': disabled },
					'rounded-sm',
					'select-none',
					disabled ?
						stateActive || checked ? 'shadow-[inset_0_0_0_2px_rgb(166,166,166)]' :
							'shadow-[inset_0_0_0_1px_rgb(166,166,166)]' :
						stateActive || checked ? 'shadow-[inset_0_0_0_2px_rgb(2,81,105)]' :
							'shadow-[inset_0_0_0_1px_rgb(2,81,105)]',
					'outline-action-secondary',
					disabled ? 'text-neutral-disabled' : 'text-action-secondary',
					'transition-colors',
					className,
				))}
			>
				<Div
					className={[
						checked ?
							error
								? 'bg-[#A0002D]'
								: disabled
									? 'bg-surface-action-disabled'
									: 'bg-surface-action-primary-active'
							: undefined,
						'border-sm',
						'border-solid',
						disabled ? 'border-action-disabled' :
							error ? 'border-feedback-error'
								: 'border-action-primary',
						'gap-xs',
						'h-[var(--design-font-xl)]',
						'flex',
						'items-center',
						'justify-center',
						'pointer-events-none',
						'rounded-[4px]',
						'text-neutral-inverted',
						'w-[var(--design-font-xl)]',
					]}
					forceRender
				>
					<FontAwesomeIconWrapper // mark
						className={cx([
							disabled ? 'text-action-disabled' : undefined,
							checked === false ? 'opacity-0' : 'opacity-100',
						])}
						// className={cn({
						// 	color: disabled ? 'borderActionDisabled' : undefined,
						// 	// cursor: 'pointer',
						// 	opacity: checked === false ? 0 : 100,
						// })}
						icon={checked === 'mixed' ? faMinus : faCheck}
					/>
				</Div>
				{label}
				{icon ? <FontAwesomeIconWrapper className={`text-xl ${iconColor}`} icon={icon}/> : null}
			</Label>
		</div>
	);
});
